import { useSelector } from 'react-redux';
import { createContext, useContext } from 'react';
import { selectEnabledPreviews } from 'modules/app/selectors';
import { selectFeatureFlags } from 'core/userContext/selectors';
import noop from 'utils/noop';
import {
  EspAccount,
  EspAccountWithCustomMTAConfig,
  SERVICE_PROVIDERS,
} from 'modules/graphql/types';
import {
  MESSAGE_MEDIUMS,
  EmailChannel,
  MessageType,
  SmsSender,
  VerificationProfile,
} from '../types';
import {
  hasNoBindings,
  getUnconnectedPlatforms,
  getConnectedPlatforms,
  accumulateEspAccountsBySendingPlatform,
  accumulateEspAccountsBySendingPlatformWithIterableSms,
} from '../utils';
import {
  SENDING_PLATFORMS,
  SendingPlatform,
  ITERABLE_SMS,
  ServiceProvidersBySendingPlatform,
  CUSTOM_PUSH,
} from '../constants';
import { CUSTOM_SEND_CONTROL_PUSH_UI } from '../previewKeys';

export type SendingPlatformsQueryContextState = {
  emailChannels: EmailChannel[];
  espAccounts: (EspAccount | EspAccountWithCustomMTAConfig)[];
  espDefinitions: SERVICE_PROVIDERS[];
  hasError: boolean;
  isLoadingEspAccounts: boolean;
  isLoadingEspDefinitions: boolean;
  isLoadingVerificationProfiles: boolean;
  messageTypes: MessageType[];
  refetchEmailChannels: () => void;
  refetchEspAccounts: () => void;
  refetchMessageTypes: () => void;
  refetchSmsSenders: () => void;
  refetchVerificationProfiles: () => void;
  smsSenders: SmsSender[];
  verificationProfiles: VerificationProfile[];
};

const defaultState = {
  emailChannels: [],
  espAccounts: [],
  espDefinitions: [],
  hasError: false,
  isLoadingEspAccounts: false,
  isLoadingEspDefinitions: false,
  isLoadingVerificationProfiles: false,
  messageTypes: [],
  refetchEmailChannels: noop,
  refetchEspAccounts: noop,
  refetchMessageTypes: noop,
  refetchSmsSenders: noop,
  refetchVerificationProfiles: noop,
  restEspAccounts: [],
  smsSenders: [],
  verificationProfiles: [],
};

export const SendingPlatformsQueryContext =
  createContext<SendingPlatformsQueryContextState>(defaultState);

export const useSendingPlatformsQueryContext = () => {
  const { SendingPlatformsIterableSMSUI = false, CustomSendControlPushUI = false } =
    useSelector(selectFeatureFlags);

  const isCustomSendControlPushUIEnabled = useSelector(selectEnabledPreviews).includes(
    CUSTOM_SEND_CONTROL_PUSH_UI,
  );

  const {
    espAccounts,
    espDefinitions,
    hasError,
    emailChannels,
    messageTypes,
    smsSenders,
    verificationProfiles,
    refetchEspAccounts,
    refetchEmailChannels,
    refetchMessageTypes,
    refetchSmsSenders,
    refetchVerificationProfiles,
    isLoadingEspAccounts,
    isLoadingEspDefinitions,
    isLoadingVerificationProfiles,
  } = useContext(SendingPlatformsQueryContext);

  const espAccountsById = espAccounts.reduce(
    (acc, espAccount) => ({ ...acc, [espAccount.id]: espAccount }),
    {} as Record<number, EspAccount>,
  );

  const accEspAccounts = SendingPlatformsIterableSMSUI
    ? accumulateEspAccountsBySendingPlatformWithIterableSms
    : accumulateEspAccountsBySendingPlatform;
  const espAccountsBySendingPlatform = espAccounts.reduce(
    accEspAccounts,
    {} as Record<SendingPlatform, EspAccount[]>,
  );

  const availablePlatforms = SENDING_PLATFORMS.filter((sendingPlatform) => {
    /**
     * Only those platforms returned in the espDefinitions shoudld be shown and interactable.
     */
    /** IterableSMS should be included only if the SendingPlatformsIterableSMSUI FF is on */
    if (sendingPlatform === ITERABLE_SMS) return Boolean(SendingPlatformsIterableSMSUI);
    /** IterableSMS should be included only if the CustomSendControlPushUI FF is on */
    if (sendingPlatform === CUSTOM_PUSH)
      return Boolean(CustomSendControlPushUI || isCustomSendControlPushUIEnabled);
    const serviceProviders = ServiceProvidersBySendingPlatform[sendingPlatform];
    return espDefinitions.some((esp) => serviceProviders.includes(esp));
  });

  const connectedPlatforms = getConnectedPlatforms(
    availablePlatforms,
    espAccountsBySendingPlatform,
  );

  const unconnectedPlatforms = getUnconnectedPlatforms(availablePlatforms, connectedPlatforms);

  const hasEspAccounts = (sendingPlatform: SendingPlatform) =>
    Boolean(espAccountsBySendingPlatform[sendingPlatform]?.length);

  const selectEspAccount = (espAccountId: number) => espAccountsById[espAccountId];

  const selectDefaultSender = () => espAccounts.find(({ isDefault }) => isDefault);

  const isSESDefault = () => !selectDefaultSender();

  const channels =
    emailChannels.map((emailChannel: EmailChannel) => {
      const channelMessageTypes =
        (messageTypes as MessageType[]).filter(
          ({ messageChannelId }) => messageChannelId === emailChannel.id,
        ) || [];
      return {
        ...emailChannel,
        messageTypes: channelMessageTypes,
      };
    }) || [];

  const selectEmailChannels = (espAccount: EspAccount) =>
    channels.filter(({ messageMedium, espBindings = [] }) => {
      /**
       * If the channel does not have any explicit bindings, it will be bound to
       * whichever ESP account is set as default.
       */
      if (messageMedium !== MESSAGE_MEDIUMS.EMAIL) return false;
      if (!espBindings.length) return espAccount.isDefault;
      return espBindings.some((espBinding) => espBinding.espAccountId === espAccount.id);
    });

  const selectSESChannels = () => {
    /**
     * If espBindings is undefined, this means that the channel is bound to the
     * default sender. If no default sender is explicitly set, Iterable SES is
     * the de facto default.
     */
    if (!isSESDefault()) return [];
    return channels.filter(
      (channel) => channel.messageMedium === MESSAGE_MEDIUMS.EMAIL && hasNoBindings(channel),
    );
  };

  const hasEmailChannels = (espAccount: EspAccount) => selectEmailChannels(espAccount).length > 0;

  const senders =
    smsSenders.map((smsSender: SmsSender) => {
      const messageType = (messageTypes as MessageType[]).find(
        ({ id }) => id === smsSender.messageTypeId,
      );
      return messageType ? { ...smsSender, messageType } : smsSender;
    }) || [];

  const selectSmsSenders = (espAccountId: number) =>
    (senders as SmsSender[]).filter((sender) => sender.espAccountId === espAccountId);

  const hasSenders = (espAccountId: number) => selectSmsSenders(espAccountId).length > 0;

  const selectVerificationProfilesByProvider = (sendingPlatform: SendingPlatform) => {
    const relevantEspAccounts = espAccountsBySendingPlatform[sendingPlatform];
    return verificationProfiles.filter((verificationProfile) =>
      relevantEspAccounts.find((esp) => esp.id === verificationProfile.espAccountId),
    );
  };

  const selectVerificationProfilesForEspAccount = (espAccountId: number) =>
    verificationProfiles.filter(
      (verificationProfile) => verificationProfile.espAccountId === espAccountId,
    );

  const smsMessageTypes = messageTypes.filter((messageType) => {
    const channel = emailChannels.find(
      (emailChannel) => emailChannel.id === messageType.messageChannelId,
    );
    return channel?.messageMedium === MESSAGE_MEDIUMS.SMS;
  });

  return {
    connectedPlatforms,
    unconnectedPlatforms,
    messageTypes,
    smsMessageTypes,
    selectDefaultSender,
    selectEspAccount,
    espAccountsBySendingPlatform,
    espDefinitions,
    hasEspAccounts,
    hasError,
    selectEmailChannels,
    isSESDefault,
    selectSESChannels,
    hasEmailChannels,
    selectSmsSenders,
    hasSenders,
    verificationProfiles,
    selectVerificationProfilesByProvider,
    selectVerificationProfilesForEspAccount,
    refetchEspAccounts,
    refetchEmailChannels,
    refetchMessageTypes,
    refetchSmsSenders,
    refetchVerificationProfiles,
    isLoadingEssentialData: isLoadingEspAccounts || isLoadingEspDefinitions,
    isLoadingVerificationProfiles,
  };
};
